import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BlogCard from './components/BlogCard';
import FilterPanel from './components/FilterPanel';
import SearchBar from './components/SearchBar';
import CategoryChips from './components/CategoryChips';
import Pagination from './components/Pagination';
import { blogService } from '../../services/blogService';

const BlogPosts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const postsPerPage = 9;

  // Calculate dynamic category counts from actual blog posts
  const categories = useMemo(() => {
    const categoryMap = {
      'web-development': { id: 'web-development', name: 'Cybersecurity', count: 0 },
      'design': { id: 'design', name: 'Design', count: 0 },
      'career': { id: 'career', name: 'Career Tips', count: 0 },
      'technology': { id: 'technology', name: 'Technology', count: 0 },
      'productivity': { id: 'productivity', name: 'Productivity', count: 0 },
      'tutorials': { id: 'tutorials', name: 'Tutorials', count: 0 }
    };

    // Count posts per category
    blogPosts?.forEach((post) => {
      if (post?.categoryId && categoryMap?.[post?.categoryId]) {
        categoryMap[post.categoryId].count++;
      }
    });

    return Object.values(categoryMap);
  }, [blogPosts]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getPublishedBlogs();
      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedPosts = useMemo(() => {
    let filtered = blogPosts;

    if (searchQuery) {
      filtered = filtered?.filter((post) =>
      post?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      post?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      post?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter((post) => post?.categoryId === selectedCategory);
    }

    const sorted = [...filtered]?.sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.publishedDate) - new Date(a.publishedDate);
        case 'oldest':
          return new Date(a.publishedDate) - new Date(b.publishedDate);
        case 'popular':
          return b?.popularity - a?.popularity;
        case 'featured':
          return (b?.featured ? 1 : 0) - (a?.featured ? 1 : 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [searchQuery, selectedCategory, sortBy, blogPosts]);

  const totalPages = Math.ceil(filteredAndSortedPosts?.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredAndSortedPosts?.slice(startIndex, startIndex + postsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
  };

  const handleNewsletterSubscribe = async (e) => {
    e?.preventDefault();
    if (!newsletterEmail?.trim()) return;

    setNewsletterLoading(true);
    setNewsletterMessage('');

    try {
      const result = await blogService?.subscribeToNewsletter(newsletterEmail);
      
      // Use the message from the service response
      setNewsletterMessage(result?.message || 'Successfully subscribed!');
      setNewsletterEmail('');
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setNewsletterMessage(err?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Blog Posts - Joe Graziano | Professional Insights & Expertise</title>
        <meta name="description" content="Explore professional insights, industry knowledge, and expert perspectives on web development, design, career growth, and technology trends." />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 py-12 md:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Professional Insights & Expertise
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                Explore articles on cybersecurity, career growth, and technology trends
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="py-6 md:py-8 border-b border-border bg-card/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-6">
              <div className="w-full lg:flex-1">
                <SearchBar
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  resultCount={filteredAndSortedPosts?.length} />

              </div>
              <Button
                variant="outline"
                onClick={() => setIsFilterOpen(true)}
                iconName="SlidersHorizontal"
                iconPosition="left"
                className="lg:hidden">

                Filters & Sort
              </Button>
            </div>

            <div className="hidden lg:block">
              <CategoryChips
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange} />

            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex gap-8">
              {/* Blog Posts Grid */}
              <div className="flex-1">
                {currentPosts?.length > 0 ?
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
                      {currentPosts?.map((post) =>
                    <BlogCard key={post?.id} post={post} featured={post?.featured} />
                    )}
                    </div>

                    {totalPages > 1 &&
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange} />

                  }
                  </> :

                <div className="text-center py-16">
                    <Icon name="FileSearch" size={64} className="mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No posts found</h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your search or filter criteria
                    </p>
                    <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setCurrentPage(1);
                    }}>

                      Clear all filters
                    </Button>
                  </div>
                }
              </div>

              {/* Desktop Filter Panel */}
              <aside className="hidden lg:block w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <FilterPanel
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onCategoryChange={handleCategoryChange}
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    isOpen={false}
                    onClose={() => {}}
                    totalPosts={blogPosts?.length} />

                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Mobile Filter Panel */}
        <FilterPanel
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          totalPosts={blogPosts?.length} />


        {/* Newsletter Section */}
        <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-accent/10 to-primary/10">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
            <Icon name="Mail" size={48} className="mx-auto mb-6 text-accent" />
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Stay Updated with Latest Insights
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Subscribe to receive weekly articles on web development, design, and career growth
            </p>
            <form onSubmit={handleNewsletterSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e?.target?.value)}
                required
                disabled={newsletterLoading}
                className="flex-1 px-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent" />

              <Button 
                variant="default" 
                iconName="Send" 
                iconPosition="right"
                type="submit"
                disabled={newsletterLoading}
              >
                {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            {newsletterMessage && (
              <p className={`mt-4 text-sm ${
                newsletterMessage?.includes('Successfully') ? 'text-green-600' : 'text-red-600'
              }`}>
                {newsletterMessage}
              </p>
            )}
          </div>
        </section>
      </main>
    </>);

};

export default BlogPosts;